"use client";

import { FEATURE_FLAGS } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { REFERRAL_QUERY_KEYS } from "./referralQueryKeys";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

type ReferralFetchMode = "full" | "minimal";

interface UseReferralsOptions {
  /**
   * - 'full' (default): fetch all referral queries (profile page)
   * - 'minimal': fetch only HUB_REFERRALS (used for auto-submit checks)
   */
  mode?: ReferralFetchMode;
  /**
   * Explicit toggle for enabling queries; defaults to hub auth gate.
   */
  enabled?: boolean;
}

/**
 * Referral data hook.
 * - mode "full" (default): fetches all referral-related data (profile page)
 * - mode "minimal": only fetches HUB_REFERRALS (for auto-submit checks)
 */
export const useReferrals = (options?: UseReferralsOptions) => {
  const mode = options?.mode ?? "full";
  const { hubUser, authenticated, auth } = useHubUser();
  const apiHelper = useApi();
  const isCodeGenerationEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.REFERRAL_CODE_GENERATION_ENABLE);

  const baseEnabled = options?.enabled ?? (authenticated && !!hubUser?.id);
  const allowFullQueries = mode === "full" && baseEnabled;
  const allowMinimalQueries = baseEnabled && (mode === "minimal" || mode === "full");

  // Query to get user's referral code
  const { data: referralCodeData, isLoading: isLoadingReferralCode } = useQuery({
    queryKey: [REFERRAL_QUERY_KEYS.REFERRAL_CODE, hubUser?.id],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_REFERRAL_CODE, {}),
    enabled: allowFullQueries,
    staleTime: 30_000,
  });

  const { data: referralCodeEligibility } = useQuery({
    queryKey: [REFERRAL_QUERY_KEYS.REFERRAL_ELIGIBILITY, hubUser?.id],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_REFERRAL_ELIGIBILITY, {}),
    enabled: allowFullQueries,
    staleTime: 30_000,
  });

  const { data: referrals, isLoading: isLoadingReferrals } = useQuery({
    queryKey: [REFERRAL_QUERY_KEYS.REFERRALS, hubUser?.id],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_REFERRALS, {}),
    enabled: allowMinimalQueries,
    staleTime: 30_000,
  });

  const { data: refereeEligibilityProgress, isLoading: isLoadingRefereeEligibilityProgress } = useQuery({
    queryKey: [REFERRAL_QUERY_KEYS.REFEREE_ELIGIBILITY_PROGRESS, hubUser?.id],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_REFEREE_ELIGIBILITY_PROGRESS, {}),
    enabled: allowFullQueries,
    staleTime: 30_000,
  });

  const { data: referrerWeeklyEligibilityProgress, isLoading: isLoadingReferrerWeeklyEligibilityProgress } = useQuery({
    queryKey: [REFERRAL_QUERY_KEYS.REFERER_WEEKLY_ELIGIBILITY_PROGRESS, hubUser?.id],
    queryFn: () => apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_REFERER_WEEKLY_ELIGIBILITY_PROGRESS, {}),
    enabled: allowFullQueries,
    staleTime: 30_000,
  });

  const getRefereeIsEligible = () => {
    if (!refereeEligibilityProgress) {
      return false;
    }
    return (
      (refereeEligibilityProgress?.tradeVolumeUSD?.current || 0) >=
        (refereeEligibilityProgress?.tradeVolumeUSD?.target || 0) &&
      (refereeEligibilityProgress?.exchangeAccountCount?.current || 0) >=
        (refereeEligibilityProgress?.exchangeAccountCount?.target || 0)
    );
  };

  return {
    referralCode: referralCodeData?.referralCode || null,
    isLoadingReferralCode,
    isCodeGenerationEnabled,
    referralCodeEligibility,
    // Expose shared helpers
    hasReferrer: !!referrals?.referralAsReferee,
    referrerReferrals: referrals?.referralsAsReferrer,
    refereeReferral: referrals?.referralAsReferee,
    isLoadingReferrals,

    refereeEligibilityProgress,
    refereeIsEligible: getRefereeIsEligible(),
    isLoadingRefereeEligibilityProgress,

    referrerWeeklyEligibilityProgress,
    isLoadingReferrerWeeklyEligibilityProgress,
  };
};
